import type { ReactNode } from 'react';
import { isApiError } from '@pc/api/client';
import { ApiErrorNotice } from './ApiErrorNotice';
import { useMe, loginUrl } from '../auth/useMe';
import { Card, Loading } from './ui';

/**
 * Answers "is there a session at all?" once, for the whole app.
 *
 * Screens still handle their own 401s — a session can expire while someone is
 * mid-task, and a panel that fires its own request has to cope. But without a
 * gate here, a signed-out visit to a multi-panel screen renders "You are signed
 * out" once per panel, which reads as three separate faults instead of one
 * expired session.
 *
 * `useMe` is shared through the query cache, so this costs no extra request:
 * screens that call it themselves get the same cached answer.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const me = useMe();

  if (me.isPending) return <Loading what="your session" />;

  if (me.isError) {
    if (isApiError(me.error) && me.error.kind === 'unauthenticated') {
      return (
        <Card title="Sign in required">
          <p>Platform Control needs an active session.</p>
          <p>
            <a href={loginUrl()}>Log in with Okta</a>
          </p>
        </Card>
      );
    }

    // Anything else — most importantly a 503, which means Okta could not be
    // reached rather than that access was refused. Never gate someone out of
    // the app over an outage without saying which it was.
    return <ApiErrorNotice error={me.error} onRetry={() => void me.refetch()} />;
  }

  return <>{children}</>;
}
